import "@/App.css";
import {
  ROUTE_HOME,
  ROUTE_INBOX,
  ROUTE_LOGIN_OUT,
  ROUTE_METRICS,
  ROUTE_PROFILE,
  ROUTE_SEARCH,
  ROUTE_TAGS,
  ROUTE_TASKS,
  ROUTE_TIMERS,
} from "@/constants/routes.constants";
import HomePage from "@/pages/Home/home.page";
import InboxPage from "@/pages/Inbox/inbox.page";
import LoggingInPage from "@/pages/LoggingIn/loggingIn.page";
import LoginPage from "@/pages/Login/login.page";
import MetricsPage from "@/pages/Metrics/metrics.page";
import PageNotFound from "@/pages/PageNotFound/pageNotFound.page";
import SearchResultsPage from "@/pages/SearchResults/searchResults.page";
import TagsPage from "@/pages/Tags/tags.page";
import TasksPage from "@/pages/Tasks/tasks.page";
import TimersPage from "@/pages/Timers/timers.page";
import UserProfilePage from "@/pages/UserProfile/userProfile.page";
import ProtectedRoute from "@/services/ProtectedRoute";
import { ThemeProvider } from "@/shadcn/components/theme-provider";
import { Toaster } from "@/shadcn/components/ui/sonner";
import { getBuildDate, titleUpdate } from "@/utils/utils";
import { useEffect, useLayoutEffect } from "react";
import { Navigate, Route, BrowserRouter as Router, Routes } from "react-router";
import Swal from "sweetalert2";

const TitleUpdater = ({ title }: { title?: string }) => {
  // Updates the page title based on the current route
  useLayoutEffect(() => {
    titleUpdate(title);
  }, [title]);
  return null;
};

function App() {
  useEffect(() => {
    const environment = import.meta.env.VITE_ENVIRONMENT;

    if (!environment) {
      console.error("VITE_ENVIRONMENT is not set");
      Swal.fire({
        title: "Error",
        html: "VITE_ENVIRONMENT is not set",
        icon: "error",
      });
      return;
    }

    if (environment !== "production") {
      return;
    }

    const buildDate = getBuildDate();

    if (!buildDate) {
      console.error("Build date not found");
    } else {
      console.warn("Build date:", buildDate.toISOString());
    }
  }, []);

  return (
    <ThemeProvider defaultTheme="system">
      <div className="App h-svh w-svw overflow-hidden">
        <Router>
          <Routes>
            <Route
              path={ROUTE_LOGIN_OUT}
              element={
                <>
                  <TitleUpdater title="Login" />
                  <LoginPage />
                </>
              }
            />
            <Route
              path="/auth/magic-link/:jwt?"
              element={
                <>
                  <TitleUpdater title="Logging in" />
                  <LoggingInPage />
                </>
              }
            />
            {/* <Route
              path={"/dev"}
              element={
                <>
                  <TitleUpdater title="Search" />
                  <SearchResultsPage />
                </>
              }
            /> */}
            <Route element={<ProtectedRoute />}>
              <Route
                path={ROUTE_HOME}
                element={
                  <>
                    <TitleUpdater title="Home" />
                    <HomePage />
                  </>
                }
              />
              <Route
                path={ROUTE_TASKS}
                element={
                  <>
                    <TitleUpdater title="Tasks" />
                    <TasksPage />
                  </>
                }
              />
              <Route
                path={`${ROUTE_TASKS}/:taskID`}
                element={
                  <>
                    <TitleUpdater title="Tasks" />
                    <TasksPage />
                  </>
                }
              />
              <Route
                path={ROUTE_TIMERS}
                element={
                  <>
                    <TitleUpdater title="Timers" />
                    <TimersPage />
                  </>
                }
              />
              <Route
                path={`${ROUTE_SEARCH}`}
                element={
                  <>
                    <TitleUpdater title="Search" />
                    <SearchResultsPage />
                  </>
                }
              />
              <Route
                path={`${ROUTE_SEARCH}/:taskID`}
                element={
                  <>
                    <TitleUpdater title="Search" />
                    <SearchResultsPage />
                  </>
                }
              />
              {/* ROUTE_INBOX */}
              <Route
                path={ROUTE_INBOX}
                element={
                  <>
                    <TitleUpdater title="Inbox" />
                    <InboxPage />
                  </>
                }
              />
              <Route
                path={`${ROUTE_INBOX}/:taskID`}
                element={
                  <>
                    <TitleUpdater title="Inbox" />
                    <InboxPage />
                  </>
                }
              />
              <Route
                path={`${ROUTE_TAGS}`}
                element={
                  <>
                    <TitleUpdater title="Tags" />
                    <TagsPage />
                  </>
                }
              />
              <Route
                path={`${ROUTE_TAGS}/:tagID`}
                element={
                  <>
                    <TitleUpdater title="Tags" />
                    <TagsPage />
                  </>
                }
              />
              <Route
                path={`${ROUTE_METRICS}`}
                element={
                  <>
                    <TitleUpdater title="Metrics" />
                    <MetricsPage />
                  </>
                }
              />
              <Route
                path={`${ROUTE_PROFILE}`}
                element={
                  <>
                    <TitleUpdater title="Profile" />
                    <UserProfilePage />
                  </>
                }
              />
              <Route path="*" element={<PageNotFound />} />
            </Route>
            <Route path="/" element={<Navigate to={ROUTE_TASKS} replace />} />
          </Routes>
        </Router>
        <Toaster position="top-right" richColors closeButton />
      </div>
    </ThemeProvider>
  );
}

export default App;
